import { useState } from 'react';

import { calendarDay, clockTime, shareText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { Meter } from '../../shared/ui/meter';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import {
  daysToQuota,
  formatBytes,
  growthText,
  ofText,
  quotaText,
  type TRetentionUsage,
  type TStorageLevels,
} from '../../entities/retention';
import { QuotaDialog } from './quota-dialog';
import { StorageChart } from './storage-chart';

interface IStoredNowCardProps {
  usage: TRetentionUsage;
  levels: TStorageLevels | undefined;
  now: number;
  timeZone?: string;
  onBiggest: () => void;
}

export const StoredNowCard = ({ usage, levels, now, timeZone, onBiggest }: IStoredNowCardProps) => {
  const [changing, setChanging] = useState(false);
  const empty = usage.totalBytes === 0;
  const allForever =
    levels !== undefined &&
    levels.foreverCount === levels.flowCount &&
    levels.defaultPeriod === 'forever';
  const pressed = usage.overQuota || usage.lastQuota !== null;
  const eta = daysToQuota(usage, now);
  const firstDay = usage.daily[0]?.day;
  const share = usage.quotaBytes > 0 ? (usage.totalBytes / usage.quotaBytes) * 100 : 0;

  return (
    <SectionPanel
      title="Stored now"
      note={`as of ${clockTime(usage.measuredAt, timeZone)} · sizes refresh every ${usage.settings.recountMinutes} min`}
    >
      <div className="px-card py-4">
        <div className="flex items-end gap-4">
          <span className="text-display font-semibold tracking-display text-primary">
            {formatBytes(usage.totalBytes)}
          </span>
          <span className="pb-1 text-data text-muted">
            {empty
              ? 'no requests yet'
              : `${growthText(usage, now)}${allForever ? ' — nothing is ever removed' : ''}`}
          </span>
        </div>

        <div className="mt-3">
          {empty || usage.daily.length < 2 ? (
            <div className="flex h-chart-storage items-center justify-center rounded-control border border-dashed border-border-raised text-meta text-muted">
              {empty
                ? 'The chart starts with your first request.'
                : firstDay
                  ? `Tracking started ${calendarDay(`${firstDay}T12:00:00Z`, now, 'UTC')} — the chart fills in from the next day.`
                  : 'The chart starts after the first day is counted.'}
            </div>
          ) : (
            <StorageChart usage={usage} now={now} />
          )}
        </div>

        {!empty && (
          <div className="mt-3 flex flex-col gap-3 border-t border-border-sunken pt-3">
            <div className="flex items-center gap-3">
              <span className="shrink-0 text-meta whitespace-nowrap text-primary">
                Storage quota · organization
              </span>
              <Meter
                value={share}
                tone={usage.overQuota ? 'danger' : 'accent'}
                className="w-quota-meter"
              />
              <span className="font-mono text-meta whitespace-nowrap text-primary">
                {ofText(usage.totalBytes, usage.quotaBytes)} ·{' '}
                {shareText(usage.totalBytes, usage.quotaBytes)}
              </span>
              {!pressed && (
                <Button
                  variant="ghost"
                  size="xs"
                  className="ml-auto"
                  onClick={() => setChanging(true)}
                >
                  Change
                </Button>
              )}
            </div>
            {pressed && (
              <>
                <Note tone="caution">
                  {eta !== null && eta < 365
                    ? `At this pace it passes ${quotaText(usage.quotaBytes)} again in about ${Math.max(1, Math.round(eta))} d. `
                    : ''}
                  Each time, the oldest content goes first — whatever the flow’s period.
                </Note>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setChanging(true)}>
                    Change the quota
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onBiggest}>
                    Shorten the biggest flows
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {changing && (
        <QuotaDialog
          quotaBytes={usage.quotaBytes}
          totalBytes={usage.totalBytes}
          onClose={() => setChanging(false)}
        />
      )}
    </SectionPanel>
  );
};
